import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../../utils/api';
import { useAuthStore } from '../../stores/authStore';
import { Building2, Calendar, Award } from 'lucide-react';
import toast from 'react-hot-toast';
import PageHeading from '../../components/PageHeading';
import { getInspectionPeerNav } from '../../navigation/inspectionNavigation';
import { sortBySsCode } from '../../utils/ssCodeSort';

const INSPECTION_TYPES = [
  { value: 'ANNUAL', label: 'Annual' },
  { value: 'MONTHLY', label: 'Monthly' },
  { value: 'QUARTERLY', label: 'Quarterly' },
  { value: 'BIANNUAL', label: 'Bi-annual' },
];

const INSPECTION_FREQUENCIES = [
  { value: 'DAILY', label: 'Daily' },
  { value: 'MONTHLY', label: 'Monthly' },
  { value: 'SIX_MONTHLY', label: 'Six-monthly' },
  { value: 'ANNUAL', label: 'Annual' },
  { value: 'DAILY_MONTHLY', label: 'Daily / Monthly' },
  { value: 'MONTHLY_ANNUAL', label: 'Monthly / Annual' },
  { value: 'SIX_MONTHLY_ANNUAL', label: 'Six-monthly / Annual' },
];

function JobForm() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { user } = useAuthStore();
  const buildingIdFromUrl = searchParams.get('building_id');

  const [formData, setFormData] = useState({
    building_id: '',
    ss_code: '',
    ss_name: '',
    due_date: '',
    inspection_type: 'ANNUAL',
    inspection_frequency: 'ANNUAL',
  });

  const { data: buildingsData } = useQuery({
    queryKey: ['buildings'],
    queryFn: async () => {
      const response = await api.get('/buildings');
      return response.data.data || [];
    },
  });
  const buildings = buildingsData || [];

  useEffect(() => {
    if (buildingIdFromUrl && buildings.length > 0 && !formData.building_id) {
      const id = String(buildingIdFromUrl).trim();
      if (buildings.some((b) => String(b.id) === id)) {
        setFormData((prev) => ({ ...prev, building_id: id }));
      }
    }
  }, [buildingIdFromUrl, buildings, formData.building_id]);

  const { data: ssCodesData } = useQuery({
    queryKey: ['ss-codes'],
    queryFn: async ({ signal }) => {
      const response = await api.get('/workers/ss-codes', { signal });
      return response.data.data || [];
    },
  });
  const ssCodes = useMemo(
    () => sortBySsCode(ssCodesData || [], 'code'),
    [ssCodesData]
  );

  const createMutation = useMutation({
    mutationFn: async (data) => {
      const response = await api.post('/jobs', {
        buildingId: Number(data.building_id),
        ssCode: data.ss_code,
        ssName: data.ss_name,
        dueDate: data.due_date,
        inspectionType: data.inspection_type,
        inspectionFrequency: data.inspection_frequency,
      });
      return response.data;
    },
    onSuccess: (data) => {
      toast.success('Inspection created successfully');
      queryClient.invalidateQueries(['jobs']);
      const newId = data?.id;
      if (newId) {
        navigate(`/jobs/${newId}`);
      } else {
        navigate('/jobs');
      }
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to create inspection');
    },
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (name === 'ss_code') {
      const selected = ssCodes.find((s) => s.code === value);
      setFormData((prev) => ({
        ...prev,
        ss_code: value,
        ss_name: selected ? selected.name || value : value,
      }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.building_id || !formData.ss_code || !formData.ss_name || !formData.due_date || !formData.inspection_frequency) {
      toast.error('Please fill in Building, SS Code, Due Date, and Inspection Frequency');
      return;
    }
    createMutation.mutate(formData);
  };

  if (user?.role === 'WORKER') {
    navigate('/', { replace: true });
    return null;
  }

  return (
    <div>
      <PageHeading
        title="New inspection"
        subPages={getInspectionPeerNav('/jobs/new')}
        infoTitle="New inspection"
        infoChildren={
          <p>
            Creates a BWOF/SS inspection for a building: due date, SS, and inspection frequency. After creation,
            assign workers and schedule from the inspection detail screen.
          </p>
        }
      />

      <form onSubmit={handleSubmit} className="bg-card rounded-lg shadow p-6">
        <div className="space-y-6">
          <div>
            <label htmlFor="building_id" className="block text-sm font-medium text-gray-700 mb-2">
              <Building2 className="w-4 h-4 inline mr-2" />
              Building *
            </label>
            <select
              id="building_id"
              name="building_id"
              value={formData.building_id}
              onChange={handleChange}
              required
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="">Select building</option>
              {buildings.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name} {b.address ? `??${b.address}` : ''}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="ss_code" className="block text-sm font-medium text-gray-700 mb-2">
              <Award className="w-4 h-4 inline mr-2" />
              SS Code *
            </label>
            <select
              id="ss_code"
              name="ss_code"
              value={formData.ss_code}
              onChange={handleChange}
              required
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="">Select SS code</option>
              {ssCodes.map((s) => (
                <option key={s.code} value={s.code}>
                  {s.code} {s.name ? `??${s.name}` : ''}
                </option>
              ))}
            </select>
            {formData.ss_code && (
              <p className="text-xs text-gray-500 mt-1">SS Name: {formData.ss_name || '(from selection)'}</p>
            )}
          </div>

          <div>
            <label htmlFor="due_date" className="block text-sm font-medium text-gray-700 mb-2">
              <Calendar className="w-4 h-4 inline mr-2" />
              Due Date *
            </label>
            <input
              type="date"
              id="due_date"
              name="due_date"
              value={formData.due_date}
              onChange={handleChange}
              required
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>

          <div>
            <label htmlFor="inspection_type" className="block text-sm font-medium text-gray-700 mb-2">
              Inspection Type
            </label>
            <select
              id="inspection_type"
              name="inspection_type"
              value={formData.inspection_type}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              {INSPECTION_TYPES.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="inspection_frequency" className="block text-sm font-medium text-gray-700 mb-2">
              Inspection Frequency *
            </label>
            <select
              id="inspection_frequency"
              name="inspection_frequency"
              value={formData.inspection_frequency}
              onChange={handleChange}
              required
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="">Select frequency</option>
              {INSPECTION_FREQUENCIES.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="mt-8 flex items-center gap-4">
          <button
            type="submit"
            disabled={createMutation.isLoading}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
          >
            {createMutation.isLoading ? 'Creating...' : 'Create Inspection'}
          </button>
          <button
            type="button"
            onClick={() => navigate('/jobs')}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}

export default JobForm;
